// What a person can do with a message sent while Poneva works, and what the
// end of an answer does with the ones Poneva never read (CONTRACT v1). Kept
// free of React so the rules are tested directly (test/steer-ui.mjs).
import { deleteSteer, postSteer } from './client';
import type { CompleteAttachment } from '@assistant-ui/react';
import { newSteerId, steerStore, type SteerEntry, type SteerHold } from './store';

export type SteerDeps = {
  threadId: string;
  fetch: typeof fetch;
  headers: () => Record<string, string>;
  /** The confirmed stream id of this thread's running answer, or null once it ended. */
  liveStreamId: () => Promise<string | null> | string | null;
  isRunning: () => boolean;
  /** Stops the running answer without the runtime's delayed rollback, and waits. */
  cancelRun: () => Promise<void>;
  /** Whether the running answer has written or done anything yet. */
  answerStarted: () => boolean;
  /** Sends a user message as a normal turn. */
  append: (text: string, attachments: CompleteAttachment[], steerIds: string[]) => void;
  composerText: () => string;
  setComposerText: (text: string) => void;
  restoreAttachments: (attachments: CompleteAttachment[]) => Promise<void>;
  focusComposer?: () => void;
};

type Store = typeof steerStore;

// One POST per message at a time, shared by every component of the page, so an
// Edit or a Remove never races the message's own POST into the inbox.
const inFlight = new Map<string, Promise<void>>();

export function joinSteerTexts(entries: readonly Pick<SteerEntry, 'text'>[]): string {
  return entries
    .map((entry) => entry.text)
    .filter(Boolean)
    .join('\n\n');
}

const files = (entries: readonly SteerEntry[]) =>
  entries.flatMap((entry) => entry.attachments ?? []);

export function createSteerActions(deps: SteerDeps, store: Store = steerStore) {
  const { threadId } = deps;

  function find(id: string) {
    return store.snapshot(threadId).find((entry) => entry.id === id);
  }

  // Only a message still on its way may change state from an outcome: one the
  // person stopped, sent or removed meanwhile keeps what they chose.
  function stillPosting(id: string) {
    return !store.isConsumed(threadId, id) && find(id)?.state === 'posting';
  }

  async function post(entry: SteerEntry) {
    const streamId = await deps.liveStreamId();
    if (!stillPosting(entry.id)) return;
    if (!streamId) {
      store.update(threadId, entry.id, { state: 'next' });
      return;
    }
    store.update(threadId, entry.id, { streamId });
    const outcome = await postSteer(deps.fetch, deps.headers, streamId, {
      id: entry.id,
      threadId,
      text: entry.text,
    });
    if (!stillPosting(entry.id)) return;
    if (outcome.kind === 'waiting')
      store.update(threadId, entry.id, { state: 'waiting', streamId });
    else if (outcome.kind === 'credits')
      store.update(threadId, entry.id, { state: 'held', hold: 'credits' });
    else store.update(threadId, entry.id, { state: 'next' });
  }

  /** Enter while Poneva works: into the running answer, or else next. */
  function submit(text: string, attachments: CompleteAttachment[] = []) {
    const trimmed = text.trim();
    if (!trimmed && !attachments.length) return;
    const running = deps.isRunning() && !attachments.length;
    const entry: SteerEntry = {
      id: newSteerId(),
      text: trimmed,
      ...(attachments.length ? { attachments } : {}),
      state: running ? 'posting' : 'next',
    };
    store.add(threadId, entry);
    if (!running) return;
    const pending = post(entry).finally(() => inFlight.delete(entry.id));
    inFlight.set(entry.id, pending);
    return pending;
  }

  /** An uncertain reply never proves that the answer stopped waiting for this message. */
  async function withdraw(entry: SteerEntry): Promise<'removed' | 'read' | 'unconfirmed'> {
    // Block the end-of-answer drain before waiting for either network request.
    // A read chunk still consumes it if the model won the race with removal.
    store.update(threadId, entry.id, { state: 'withdrawing' });
    await inFlight.get(entry.id);
    const current = find(entry.id);
    if (!current) return 'read';
    // A lost POST reply can leave a locally "next" message in the server inbox.
    if (!current.streamId) return 'removed';
    const outcome = await deleteSteer(deps.fetch, deps.headers, current.streamId, current.id);
    if (outcome.kind === 'unconfirmed' && !deps.isRunning()) store.settle(threadId);
    return outcome.kind === 'gone' ? 'removed' : outcome.kind;
  }

  /** Edit: the text goes back into the composer, unless Poneva already read it. */
  async function edit(id: string) {
    const entry = find(id);
    if (!entry) return;
    const outcome = await withdraw(entry);
    if (outcome === 'unconfirmed') return;
    if (outcome === 'read') {
      store.consume(threadId, [id]);
      return;
    }
    await deps.restoreAttachments(entry.attachments ?? []);
    store.consume(threadId, [id]);
    const current = deps.composerText();
    deps.setComposerText(current.trim() ? `${current}\n${entry.text}` : entry.text);
    deps.focusComposer?.();
  }

  async function remove(id: string) {
    const entry = find(id);
    if (!entry) return;
    if ((await withdraw(entry)) === 'unconfirmed') return;
    store.consume(threadId, [id]);
  }

  /**
   * Send now: stop Poneva (what it wrote stays) and send every waiting message,
   * plus what is in the composer, as one message at once.
   */
  async function sendNow(extra = '', attachments: CompleteAttachment[] = []) {
    const entries = store.snapshot(threadId);
    const added = extra.trim();
    if (!entries.length && !added && !attachments.length) return;
    // Nothing written yet: its first step is the next one, so the message goes
    // in there. Stopping now would take the question back out of the chat.
    if (deps.isRunning() && !deps.answerStarted()) {
      if (added || attachments.length) await submit(added, attachments);
      return;
    }
    if (deps.isRunning()) {
      try {
        await deps.cancelRun();
      } catch {
        // Accepted entries still belong to that inbox. Reposting their text
        // under new ids would make the model read it twice.
        if (added || attachments.length) await submit(added, attachments);
        return;
      }
    }
    // A natural finish can drain the queue and start its follow-up while Stop
    // is pending. Add new text to that answer instead of starting a competing turn.
    if (deps.isRunning()) {
      if (added || attachments.length) await submit(added, attachments);
      return;
    }
    const remaining = entries.filter((entry) => find(entry.id));
    const texts = [...remaining.map((entry) => entry.text), ...(added ? [added] : [])];
    const combined = [...files(remaining), ...attachments];
    if (!texts.length && !combined.length) return;
    store.consume(
      threadId,
      remaining.map((entry) => entry.id),
    );
    deps.append(
      texts.filter(Boolean).join('\n\n'),
      combined,
      remaining.map((entry) => entry.id),
    );
  }

  /** Send, or Send anyway: what waits for the person goes now, as one message. */
  function sendHeld() {
    const entries = store.snapshot(threadId);
    if (!entries.length || deps.isRunning()) return;
    store.consume(
      threadId,
      entries.map((entry) => entry.id),
    );
    deps.append(
      joinSteerTexts(entries),
      files(entries),
      entries.map((entry) => entry.id),
    );
  }

  async function clearAll() {
    // Restored messages may still be in a live inbox. Clear has the same
    // confirmed-withdrawal boundary as Remove, including uncertain replies.
    await Promise.all(store.snapshot(threadId).map((entry) => remove(entry.id)));
  }

  /**
   * Stop before Poneva wrote anything takes the question back into the composer
   * (the runtime's own Stop); messages added meanwhile go back with it.
   */
  async function returnToComposer() {
    const entries = store.snapshot(threadId);
    if (!entries.length) return;
    const attachments = files(entries);
    if (attachments.length) await deps.restoreAttachments(attachments);
    store.consume(
      threadId,
      entries.map((entry) => entry.id),
    );
    const current = deps.composerText().trim();
    deps.setComposerText(
      [current, ...entries.map((entry) => entry.text)].filter(Boolean).join('\n\n'),
    );
  }

  /** The person pressed Stop (or Esc): nothing they added is sent or lost. */
  function holdForStop() {
    store.holdAll(threadId, 'stopped');
  }

  /**
   * The answer is over and nothing runs: unread messages go next as one
   * message, unless something must hold them for the person.
   */
  function drain(hold: SteerHold | null): 'sent' | 'held' | 'none' {
    if (deps.isRunning()) return 'none';
    store.settle(threadId);
    const ready = store.snapshot(threadId).filter((entry) => entry.state === 'next');
    if (!ready.length) return 'none';
    if (hold) {
      store.holdAll(threadId, hold);
      return 'held';
    }
    store.consume(
      threadId,
      ready.map((entry) => entry.id),
    );
    deps.append(
      joinSteerTexts(ready),
      files(ready),
      ready.map((entry) => entry.id),
    );
    return 'sent';
  }

  return {
    submit,
    edit,
    remove,
    sendNow,
    sendHeld,
    clearAll,
    holdForStop,
    returnToComposer,
    drain,
  };
}

export type SteerActions = ReturnType<typeof createSteerActions>;
